import { message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import { resyncBusinessEntity } from "@/api/entities";
import type { BusinessEntityDetailVO } from "@/api/entities/type";

import { ENTITY_SYNC_COOLDOWN_MS } from "../constants";

/** 工商同步按钮阶段 */
export type EntitySyncPhase = "idle" | "syncing" | "success" | "failed" | "cooldown";

/** useEntitySync 入参 */
export interface UseEntitySyncOptions {
  /** 当前实体 ID */
  entityId?: string;
  /** 弹窗是否打开 */
  open?: boolean;
  /** 同步渠道 dict_code，优先沿用详情中的值 */
  syncProvider?: string;
  /** 详情中的最后同步时间，用于恢复冷却态 */
  lastSyncAt?: string;
  /** 同步成功后回调（写入最新详情） */
  onSyncSuccess?: (detail: BusinessEntityDetailVO) => void;
}

/** 取本地记录与详情 lastSyncAt 中较新的同步时间戳 */
const resolveLastSyncTimestamp = (
  entityId: string,
  syncTimestamps: Record<string, number>,
  lastSyncAt?: string,
): number => {
  const localTs = syncTimestamps[entityId] ?? 0;
  const serverTs = lastSyncAt && dayjs(lastSyncAt).isValid() ? dayjs(lastSyncAt).valueOf() : 0;
  return Math.max(localTs, serverTs);
};

/**
 * 工商同步按钮状态：调用 resyncEntity 刷新第三方工商数据
 */
export const useEntitySync = ({
  entityId,
  open = false,
  lastSyncAt,
  onSyncSuccess,
}: UseEntitySyncOptions) => {
  const [syncPhase, setSyncPhase] = useState<EntitySyncPhase>("idle");
  /** 本次会话内成功同步的时间戳，用于 5 天冷却 */
  const [syncTimestamps, setSyncTimestamps] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());
  const timerRef = useRef<number | null>(null);
  /** 成功/失败反馈定时器，卸载时清理 */
  const feedbackTimerRef = useRef<number | null>(null);

  /** 每秒刷新倒计时 */
  useEffect(() => {
    timerRef.current = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  /** 打开弹窗时根据历史同步时间恢复冷却态 */
  useEffect(() => {
    if (!open || !entityId) return;
    const lastSync = resolveLastSyncTimestamp(entityId, syncTimestamps, lastSyncAt);
    if (!lastSync) {
      setSyncPhase("idle");
      return;
    }
    const remaining = Math.max(0, ENTITY_SYNC_COOLDOWN_MS - (Date.now() - lastSync));
    setSyncPhase(remaining > 0 ? "cooldown" : "idle");
  }, [entityId, lastSyncAt, open, syncTimestamps]);

  /** 关闭弹窗重置同步中/成功/失败态 */
  useEffect(() => {
    if (open) return;
    setSyncPhase("idle");
  }, [open]);

  /** 剩余冷却毫秒 */
  const getSyncRemaining = useCallback(
    (id: string) => {
      const lastSync = resolveLastSyncTimestamp(id, syncTimestamps, lastSyncAt);
      if (!lastSync) return 0;
      return Math.max(0, ENTITY_SYNC_COOLDOWN_MS - (now - lastSync));
    },
    [lastSyncAt, now, syncTimestamps],
  );

  /** 触发工商同步 */
  const handleSync = useCallback(async () => {
    if (!entityId || syncPhase !== "idle") return;
    setSyncPhase("syncing");
    try {
      const res = await resyncBusinessEntity(entityId);
      setSyncPhase("success");
      if (res.data) {
        onSyncSuccess?.(res.data);
      }
      message.success("工商数据同步成功");
      // 成功反馈 2 秒后进入冷却倒计时
      feedbackTimerRef.current = window.setTimeout(() => {
        setSyncTimestamps((prev) => ({ ...prev, [entityId]: Date.now() }));
        setSyncPhase("cooldown");
      }, 2000);
    } catch {
      setSyncPhase("failed");
      message.error("工商数据同步失败，请稍后重试");
      feedbackTimerRef.current = window.setTimeout(() => setSyncPhase("idle"), 2000);
    }
  }, [entityId, onSyncSuccess, syncPhase]);

  return {
    syncPhase,
    getSyncRemaining,
    handleSync,
  };
};
